import {classificationTypes, disseminationOptionTypes, otherDissemOptionTypes, relToOptionTypes, sciOptionTypes, fgiOptionTypes, lacOptionTypes,
    topicOptionValueTypes, regionOptionValueTypes, disseminationOptionsOrder, otherDissemOptionsOrder, relToOptionsOrder, sciOptionsOrder,
    fgiOptionsOrder, lacOptionsOrder, topicOptionsValuesOrder, regionOptionsValuesOrder} from './constants/definedTypes';
import {Utilities} from './utils/Utilities';
import {ValidateObject} from './utils/Validate';

export class ClassificationObject {
    // Variable Reference (Set in Constructor)
    /*
    protected classification?: classificationTypes;
    protected banner?: string;
    protected disseminationOptions?: disseminationOptionTypes;
    protected relToOptions?: relToOptionTypes;
    protected sciOptions?: sciOptionTypes;
    protected fgiOptions?: fgiOptionTypes;
    protected classificationDate?: string;
    protected declassificationDate?: string;
    protected isRawSigint?: boolean;
    protected isEvaluatedAndMinimized: boolean;
    protected isDisseminable: boolean;
    protected lacOptions?: lacOptionTypes;
    protected accmValue: string;
    protected topicOptions?: topicOptionValueTypes;
    protected regionOptions?: regionOptionsValueTypes;
    protected coiOptions?: string[];
    protected brfs?: string[];
    protected organization?: string;
    protected classifiedBy?: string;
    */

    protected validator: ValidateObject;

    constructor(protected classification?: classificationTypes, protected banner?: string, protected disseminationOptions?: disseminationOptionTypes,
        protected relToOptions?: relToOptionTypes, protected sciOptions?: sciOptionTypes, protected fgiOptions?: fgiOptionTypes, protected classificationDate?: string,
        protected declassificationDate?: string, protected isRawSigint?: boolean, protected isEvaluatedAndMinimized?: boolean, protected isDisseminable?: boolean,
        protected lacOptions?: lacOptionTypes, protected accmValue?: string, protected topicOptions?: topicOptionValueTypes, protected regionOptions?: regionOptionValueTypes,
        protected coiOptions?: string[], protected brfs?: string[], protected organization?: string, protected classifiedBy?: string) {
        this.validator = new ValidateObject([], []);
    }

    // Reference to Class Name after code has been minimized
    getClassName(): string {
        return 'ClassificationObject';
    }

    getClassification(): string | undefined {
        return this.classification;
    }

    setClassification(classification?: classificationTypes): void {
        this.classification = classification;
    }

    getBanner(): string | undefined {
        // Remove Unknown Options
        const removeUnknown = true;
        // Classification
        let tempBanner = this.getClassification();

        // SCI Options
        const sciItems = Utilities.getSafe(() => this.getSCIOptions(removeUnknown), undefined);
        if (sciItems !== undefined && sciItems!.length !== 0) {
            // find Compartments within the SCI Controls for properer ordering according to
            // DoDM 5200.01-v2, February 24, 2012
            // Change 3, 05/14/2019 Enclosure 4, 6 d Use a hyphen without interjected spaces
            // to separate an SCI control system name and its compartment(s) when applicable,
            // e.g. "SI-GAMMA" or to separate multiple compartments from each other, e.g.
            // "SI-G-XXX". List multiple compartments alpha-numerically.
            const sciCompartmentObj = {};
            const sciArrayFinal: string[] = [];

            for (let i=0; i<sciItems.length; i++) {
                const sci = sciItems[i];

                const sciSplit = sci.split('-');
                // determine if there is a compartment
                if (sciSplit.length > 1) {
                    // If 1st compartment for a sci control create an array
                    if (!(sciSplit[0] in sciCompartmentObj)) {
                        sciCompartmentObj[sciSplit[0]] = [];
                    }
                    // Add the compartment to the list for the control
                    sciCompartmentObj[sciSplit[0]].push(sciSplit[1]);
                } else {
                    // If no compartment add to the final array
                    sciArrayFinal.push(sci);
                }
            }

            // Build the - delimited strings for SCI Controls
            for (const key in sciCompartmentObj) {
                // If there was a version w/o compartements added to the final array remove it
                const sciIdx = sciArrayFinal.indexOf(key);
                if (sciIdx >=0) {
                    sciArrayFinal.splice(sciIdx, 1);
                }
                // Sort the compartments alpha-numerically and add to the final array
                sciArrayFinal.push(key + '-' + sciCompartmentObj[key].sort().join('-'));
            }

            if (sciArrayFinal.length > 0) {
                tempBanner += '//' + sciArrayFinal.sort().join('/');
            }
        }

        // FGI Options
        const FGIItems = Utilities.getSafe(() => this.getFGIOptions(removeUnknown), undefined);
        if (FGIItems !== undefined && FGIItems!.length !== 0) {
            // MDEV-1555: Accounting for the possibility of no fgis selected (closed FGI)
            if (FGIItems[0].indexOf('CONCEALED') >= 0) {
                tempBanner += '//FGI';
            } else {
                const fgis = FGIItems!.join(' ');
                tempBanner += '//FGI ' + fgis;
            }
        }

        // Dissemination Control order is specific specified in DoDM 5200.01-v2, February 24, 2012
        // Change 3, 05/14/2019 Enclosure 4, 1b, (4)(a) Multiple entries may be chosen from the dissemination
        // control categories if applicable. If multiple entries are used, they are listed in the order which
        // they appear in Fig 25. items in () aren't in the Figure
        //
        // 8. Dissemination Controls
        // FOR OFFICICIAL USE ONLY (FOUO)
        // ORIGINATOR CONTROLLED (ORCON,ORCON-USGOV)
        // CONTROLLED IMAGERY (IMCON)
        // NOT RELEASABLE TO FOREIGN NATIONALS (NOFORN)
        // CAUTION-PROPRIETARY INFORMATION INVOLVED (PROPIN)
        // AUTHORIZED FOR RELEASE TO (RELTO)
        // RELEASABLE BY INFORMATION DISCLOSURE OFFICIAL (RELIDO)
        // FOREIGN INTELLIIGENCE SURVEILLANCE ACT (FISA)
        // DISPLAY ONLY
        //
        // The Intelligence Community Markings System Register and Manual
        // Effective: 30 August 2019 it specifies RISK SENSITIVE (RSEN) before FOUO
        // in the Dissemination Controls Section. Section 9 Other Dissemination
        // Controls specifies that the following controls should be separated from the
        // Dissemination Controls section by // and be in the following order:
        //
        // 9. Other Dissemination Controls
        // Limdis, Exclusive Distribution (EXDIS), No Distribution (NODIS), Sensitive
        // But Unclassified (SBU), Law Enforcement Sensitive (LES), and SSI
        // Get Array of dissemination Items ['FOUO', 'REL TO', 'LIMDIS']
        const disseminationItems = Utilities.getSafe(() => this.getDisseminationOptions(removeUnknown), undefined);
        if (disseminationItems !== undefined && disseminationItems!.length !== 0) {
            const dissemValues = [] as disseminationOptionTypes;
            const otherDissemValues = [] as otherDissemOptionTypes;
            disseminationItems.forEach((element) => {
                if (otherDissemOptionsOrder.indexOf(element) >= 0) {
                    otherDissemValues.push(element);
                } else {
                    dissemValues.push(element);
                }
            });
            if (dissemValues.length > 0) {
                // Build out banner string
                // REL TO shows list of countires separated by a ', '
                tempBanner += '//' + dissemValues!.map((obj) => {
                    if (obj === 'REL TO') {
                        return obj + ' ' + this.getRelToOptions()!.map((country) => country).join(', ');
                    } else {
                        return obj;
                    }
                }).join('/');
            }
            if (otherDissemValues.length > 0) {
                // Build out banner string
                tempBanner += '//' + otherDissemValues.join('/');
            }
        }

        // ACCM
        const accmValue = Utilities.getSafe(() => this.getACCMValue(), undefined);
        if (accmValue !== undefined) {
            if (Array.isArray(accmValue)) {
                tempBanner += '//ACCM-' + accmValue.join('/');
            } else {
                tempBanner += '//ACCM-' + accmValue;
            }
        }

        // Topics
        if (Utilities.getSafe(() => this.getTopicOptions(), undefined) !== undefined) {
            let topics = '';
            if (this.getTopicOptions()!.length !== 0) {
                topics = this.getTopicOptions()!.join(', ');
                tempBanner += ' ; Topics: '+ topics;
            }
        }

        // Regions
        if (Utilities.getSafe(() => this.getRegionOptions(), undefined) !== undefined) {
            let regions = '';
            if (this.getRegionOptions()!.length !== 0) {
                regions = this.getRegionOptions()!.join(', ');
                tempBanner += ' ; Regions: '+ regions;
            }
        }

        // SIGINT
        if (Utilities.getSafe(() => this.getIsRawSigint(), undefined) !== undefined && this.getIsRawSigint()) {
            tempBanner += ' ; SIGINT:Raw';
        } else if (Utilities.getSafe(() => this.getIsEvaluatedAndMinimized(), undefined) !== undefined && this.getIsEvaluatedAndMinimized()) {
            tempBanner += ' ; SIGINT:Is Evaluated And Minimized';
            if (Utilities.getSafe(() => this.getIsDisseminable(), undefined) !== undefined && this.getIsDisseminable()) {
                tempBanner += ' ; Is Disseminable';
            }
        }

        // LACs
        if (Utilities.getSafe(() => this.getLACOptions(), undefined) !== undefined) {
            let lacs = '';
            if (this.getLACOptions()!.length !== 0) {
                lacs = this.getLACOptions()!.join(' ; ');
                tempBanner += ' ; ; '+lacs;
            }
        }
        return tempBanner;
    }

    setBanner(banner?: string): void {
        this.banner = banner;
    }

    getDisseminationOptions(removeUnknown?: boolean): disseminationOptionTypes | undefined {
        // Sort Array based on order from definedTypes.ts -> disseminationOptionsOrder
        // Will Remove any dissemination Items that have not been defined if removeUnknown = true
        const disseminationItems = Utilities.getSafe(() => Utilities.sortByDefinedOrder(this.disseminationOptions, disseminationOptionsOrder, removeUnknown), undefined);
        return disseminationItems;
    }

    setDisseminationOptions(disseminationOptions?: disseminationOptionTypes): void {
        this.disseminationOptions = disseminationOptions;
    }

    getRelToOptions(removeUnknown?: boolean): relToOptionTypes | undefined {
        // REL TO order is specific specified in DoDM 5200.01-v2, February 24, 2012
        // Change 3, 05/14/2019 Enclosure 4, 10 d (4) The format for REL TO banner and portion markings
        // is [classification]//REL TO [country codes]. Trigraphic country code shall be listed first
        // (USA first, followed by other countries in alphabetical order), followed by coalition or
        // international organization tetragraph codes in alphabetical order. Each code shall be
        // separated from the next by a comma and space.
        if (this.relToOptions !== undefined && this.relToOptions.length > 0) {
            const relToArray = [...this.relToOptions];

            // remove the default of USA b/c it goes first in the trigraphic order
            const usaCtryStr = 'USA';
            const usaCtryStrIdx = relToArray.indexOf(usaCtryStr);
            if (usaCtryStrIdx >=0) {
                relToArray.splice(usaCtryStrIdx, 1);
            }
            let relToCtry = [] as relToOptionTypes;
            const relToTetra = [] as relToOptionTypes;
            for (let j=0; j<relToArray.length; j++) {
                if (relToArray[j].length == 3) {
                    relToCtry.push(relToArray[j]);
                } else {
                    relToTetra.push(relToArray[j]);
                }
            }

            // sort the 2 categories of graphs, see above
            relToCtry.sort();
            relToTetra.sort();

            relToCtry = relToCtry.concat(relToTetra);
            if (usaCtryStrIdx >=0) {
                relToCtry.unshift(usaCtryStr);
            }

            return relToCtry;
        }
        return this.relToOptions;
    }

    setRelToOptions(relToOptions?: relToOptionTypes): void {
        this.relToOptions = relToOptions;
    }

    getSCIOptions(removeUnknown?: boolean): sciOptionTypes | undefined {
        // Sort Array based on order from definedTypes.ts -> sciOptionsOrder
        // Will Remove any SCI Items that have not been defined if removeUnknown = true
        const sciOptionItems = Utilities.getSafe(() => Utilities.sortByDefinedOrder(this.sciOptions, sciOptionsOrder, removeUnknown), undefined);

        // SCI Control order is specific specified in DoDM 5200.01-v2, February 24, 2012
        // Change 3, 05/14/2019 Enclosure 4, 6c Multiple SCI control system entries may be used if applicable.
        // When multiple entries, (whether published or unpublished) are used, list them alphabetically. Use
        // a single forward slash (/) as the separator between multiple SCI control system entries (see Figure 33).
        if (sciOptionItems !== undefined) {
            sciOptionItems.sort();
        }

        return sciOptionItems;
    }

    setSCIOptions(sciOptions?: sciOptionTypes): void {
        this.sciOptions = sciOptions;
    }

    getFGIOptions(removeUnknown?: boolean): fgiOptionTypes | undefined {
        const fgiOptions = this.fgiOptions;

        // FGI order is specific specified in DoDM 5200.01-v2, February 24, 2012
        // Change 3, 05/14/2019 Enclosure 4, 9  Exept as provided in paragraph 9.e.
        // of this section, use "FGI" with the trigraphic country code and
        // international organization tetragraphs in the banner line; portion
        // markings for included FGI shall be specified in section 4 of this enclosure.
        // If multiple governments and/or international organizatinos furnished informtion,
        // the country trigraphs shall be listed in alphabetical order followed by the
        // international tetragraphs in alphabetical order, each separated by a single
        // space.

        if (fgiOptions !== undefined && fgiOptions.length > 0) {
            const fgiArray = [...fgiOptions];

            let fgiCtry = [] as fgiOptionTypes;
            const fgiTetra = [] as fgiOptionTypes;
            for (let j=0; j<fgiArray.length; j++) {
                if (fgiArray[j].length == 3) {
                    fgiCtry.push(fgiArray[j]);
                } else {
                    fgiTetra.push(fgiArray[j]);
                }
            }

            // sort the 2 categories of graphs, see above
            fgiCtry.sort();
            fgiTetra.sort();

            fgiCtry = fgiCtry.concat(fgiTetra);

            return fgiCtry;
        }
        return fgiOptions;
    }

    setFGIOptions(fgiOptions?: fgiOptionTypes): void {
        this.fgiOptions = fgiOptions;
    }

    getClassificationDate(): string | undefined {
        return this.classificationDate;
    }

    setClassificationDate(classificationDate?: string): void {
        this.classificationDate = classificationDate;
    }

    getDeClassificationDate(): string | undefined {
        return this.declassificationDate;
    }

    setDeClassificationDate(declassificationDate?: string): void {
        this.declassificationDate = declassificationDate;
    }

    getIsRawSigint(): boolean | undefined {
        return this.isRawSigint;
    }

    setIsRawSigint(isRawSigint?: boolean): void {
        this.isRawSigint = isRawSigint;
    }

    getIsEvaluatedAndMinimized(): boolean | undefined {
        return this.isEvaluatedAndMinimized;
    }

    setIsEvaluatedAndMinimized(isEvaluatedAndMinimized?: boolean): void {
        this.isEvaluatedAndMinimized = isEvaluatedAndMinimized;
    }

    getIsDisseminable(): boolean | undefined {
        return this.isDisseminable;
    }

    setIsDisseminable(isDisseminable?: boolean): void {
        this.isDisseminable = isDisseminable;
    }

    getLACOptions(): lacOptionTypes | undefined {
        // Sort Array based on order from definedTypes.ts -> lacOptionsOrder
        const lacItems = Utilities.getSafe(() => Utilities.sortByDefinedOrder(this.lacOptions, lacOptionsOrder, false), undefined);
        return lacItems;
    }

    setLACOptions(lacOptions?: lacOptionTypes): void {
        this.lacOptions = lacOptions;
    }

    getACCMValue(): string | undefined {
        return this.accmValue;
    }

    setACCMValue(accmValue?: string): void {
        this.accmValue = accmValue;
    }

    getTopicOptions(): topicOptionValueTypes | undefined {
        // Sort Array based on order from definedTypes.ts -> lacOptionsOrder
        // Will Remove any Topic Items that have not been defined if removeUnknown = true
        const topicItems = Utilities.getSafe(() => Utilities.sortByDefinedOrder(this.topicOptions, topicOptionsValuesOrder, false), undefined);
        return topicItems;
    }

    setTopicOptions(topicOptions?: topicOptionValueTypes): void {
        this.topicOptions = topicOptions;
    }

    getRegionOptions(): regionOptionValueTypes | undefined {
        // Sort Array based on order from definedTypes.ts -> lacOptionsOrder
        // Will Remove any Regions Items that have not been defined if removeUnknown = true
        const regionItems = Utilities.getSafe(() => Utilities.sortByDefinedOrder(this.regionOptions, regionOptionsValuesOrder, false), undefined);
        return regionItems;
    }

    setRegionOptions(regionOptions?: regionOptionValueTypes): void {
        this.regionOptions = regionOptions;
    }

    getCOIOptions(): string[] | undefined {
        return this.coiOptions;
    }

    setCOIOptions(coiOptions?: string[]): void {
        this.coiOptions = coiOptions;
    }

    getBRFs(): string[] | undefined {
        return this.brfs;
    }

    setBRFs(brfs?: string[]): void {
        this.brfs = brfs;
    }

    getOrganization(): string | undefined {
        return this.organization;
    }

    setOrganization(organization?: string): void {
        this.organization = organization;
    }

    getClassifiedBy(): string | undefined {
        return this.classifiedBy;
    }

    setClassifiedBy(classifiedBy?: string): void {
        this.classifiedBy = classifiedBy;
    }

    getAllSelections(): string | undefined {
        // Remove Unknown Options
        const removeUnknown = true;
        // Classification
        let tempBanner = this.getClassification();

        // SCI Options
        const sciItems = Utilities.getSafe(() => this.getSCIOptions(removeUnknown), undefined);
        if (sciItems !== undefined && sciItems!.length !== 0) {
            // find Compartments within the SCI Controls for properer ordering according to
            // DoDM 5200.01-v2, February 24, 2012
            // Change 3, 05/14/2019 Enclosure 4, 6 d Use a hyphen without interjected spaces
            // to separate an SCI control system name and its compartment(s) when applicable,
            // e.g. "SI-GAMMA" or to separate multiple compartments from each other, e.g.
            // "SI-G-XXX". List multiple compartments alpha-numerically.
            const sciCompartmentObj = {};
            const sciArrayFinal: string[] = [];

            for (let i=0; i<sciItems.length; i++) {
                const sci = sciItems[i];

                const sciSplit = sci.split('-');
                const sciControl = sciSplit[0];
                // determine if there is a compartment
                if (sciSplit.length > 1) {
                    const sciCompartment = sciSplit[1];
                    // If 1st compartment for a sci control create an array
                    if (!(sciControl in sciCompartmentObj)) {
                        sciCompartmentObj[sciControl] = [];
                    }
                    // Add the compartment to the list for the control
                    sciCompartmentObj[sciControl].push(sciCompartment);
                } else {
                    // If no compartment add to the final array
                    sciArrayFinal.push(sci);
                }
            }

            // Build the - delimited strings for SCI Controls
            for (const key in sciCompartmentObj) {
                // If there was a version w/o compartements added to the final array remove it
                const sciIdx = sciArrayFinal.indexOf(key);
                if (sciIdx >=0) {
                    sciArrayFinal.splice(sciIdx, 1);
                }
                // Sort the compartments alpha-numerically and add to the final array
                sciArrayFinal.push(key + '-' + sciCompartmentObj[key].sort().join('-'));
            }

            if (sciArrayFinal.length > 0) {
                tempBanner += '//' + sciArrayFinal.sort().join('/');
            }
        }

        // FGI Options
        const FGIItems = Utilities.getSafe(() => this.getFGIOptions(removeUnknown), undefined);
        if (FGIItems !== undefined && FGIItems!.length !== 0) {
            // MDEV-1555: Accounting for the possibility of no fgis selected (closed FGI)
            if (FGIItems[0].indexOf('CONCEALED') >= 0) {
                tempBanner += '//FGI';
            } else {
                const fgis = FGIItems!.map( (fgi) => fgi).join(' ');
                tempBanner += '//FGI ' + fgis;
            }
        }

        // Dissemination Control order is specific specified in DoDM 5200.01-v2, February 24, 2012
        // Change 3, 05/14/2019 Enclosure 4, 1b, (4)(a) Multiple entries may be chosen from the dissemination
        // control categories if applicable. If multiple entries are used, they are listed in the order which
        // they appear in Fig 25. items in () aren't in the Figure
        //
        // 8. Dissemination Controls
        // FOR OFFICICIAL USE ONLY (FOUO)
        // ORIGINATOR CONTROLLED (ORCON,ORCON-USGOV)
        // CONTROLLED IMAGERY (IMCON)
        // NOT RELEASABLE TO FOREIGN NATIONALS (NOFORN)
        // CAUTION-PROPRIETARY INFORMATION INVOLVED (PROPIN)
        // AUTHORIZED FOR RELEASE TO (RELTO)
        // RELEASABLE BY INFORMATION DISCLOSURE OFFICIAL (RELIDO)
        // FOREIGN INTELLIIGENCE SURVEILLANCE ACT (FISA)
        // DISPLAY ONLY
        //
        // The Intelligence Community Markings System Register and Manual
        // Effective: 30 August 2019 it specifies RISK SENSITIVE (RSEN) before FOUO
        // in the Dissemination Controls Section. Section 9 Other Dissemination
        // Controls specifies that the following controls should be separated from the
        // Dissemination Controls section by // and be in the following order:
        //
        // 9. Other Dissemination Controls
        // Limdis, Exclusive Distribution (EXDIS), No Distribution (NODIS), Sensitive
        // But Unclassified (SBU), Law Enforcement Sensitive (LES), and SSI
        // Get Array of dissemination Items ['FOUO', 'REL TO', 'LIMDIS']
        const disseminationItems = Utilities.getSafe(() => this.getDisseminationOptions(removeUnknown), undefined);
        if (disseminationItems !== undefined && disseminationItems!.length !== 0) {
            const dissemValues = [] as disseminationOptionTypes;
            const otherDissemValues = [] as otherDissemOptionTypes;
            disseminationItems.forEach((element) => {
                if (otherDissemOptionsOrder.indexOf(element) >= 0) {
                    otherDissemValues.push(element);
                } else {
                    dissemValues.push(element);
                }
            });
            if (dissemValues.length > 0) {
                // Build out banner string
                // REL TO shows list of countires separated by a ', '
                tempBanner += '//' + dissemValues!.map((obj) => {
                    if (obj === 'REL TO') {
                        return obj + ' ' + this.getRelToOptions()!.map((country) => country).join(', ');
                    } else {
                        return obj;
                    }
                }).join('/');
            }
            if (otherDissemValues.length > 0) {
                // Build out banner string
                tempBanner += '//' + otherDissemValues.join('/');
            }
        }

        const accmValue = Utilities.getSafe(() => this.getACCMValue(), undefined);
        if (accmValue !== undefined) {
            if (Array.isArray(accmValue)) {
                tempBanner += '//ACCM-' + accmValue.join('/');
            } else {
                tempBanner += '//ACCM-' + accmValue;
            }
        }

        // SIGINT Options
        if (this.getIsRawSigint()) {
            tempBanner += ' ; SIGINT:Raw';
        } else if (this.getIsEvaluatedAndMinimized()) {
            tempBanner += ' ; SIGINT:Is Evaluated And Minimized';
            if (Utilities.getSafe(() => this.getIsDisseminable(), undefined) !== undefined && this.getIsDisseminable()) {
                tempBanner += ' ; Is Disseminable';
            }
        }
        if (Utilities.getSafe(() => this.getLACOptions(), undefined) !== undefined) {
            let lacs = '';
            if (this.getLACOptions()!.length !== 0) {
                lacs = this.getLACOptions()!.join(' ; ');
                tempBanner += ' ; '+lacs;
            }
        }

        // Topic Options
        const topicItems = Utilities.getSafe(() => this.getTopicOptions(), undefined);
        if (topicItems !== undefined && topicItems!.length !== 0) {
            const topics = topicItems!.map( (topic) => topic).join(', ');
            tempBanner += ' ; Topics: ' + topics;
        }

        // Region Options
        const regionItems = Utilities.getSafe(() => this.getRegionOptions(), undefined);
        if (regionItems !== undefined && regionItems!.length !== 0) {
            const regions = regionItems!.map( (region) => region).join(', ');
            tempBanner += ' ; Regions: ' + regions;
        }

        // COIs
        // const coiItems = Utilities.getSafe(() => this.getCOIOptions(), undefined);
        // if(coiItems !== undefined && coiItems!.length !== 0) {
        if (this.getCOIOptions()) {
            // let cois = coiItems.join(', ');
            tempBanner += ' ; COIs: ' + this.getCOIOptions();
        }

        // Briefings
        // const brfItems = Utilities.getSafe(() => this.getBRFs(), undefined);
        // if(brfItems !== undefined && brfItems!.length !== 0) {
        if (this.getBRFs()) {
            // let brfs = brfItems.join(', ');
            tempBanner += ' ; Briefings: ' + this.getBRFs();
        }

        // Organization
        if (this.getOrganization()) {
            tempBanner += ' ; Organization: ' + this.getOrganization();
        }

        return tempBanner;
    }

    // Return JSON string of Object
    serialize(): string {
        return JSON.stringify(this, ['classification', 'banner', 'disseminationOptions', 'relToOptions', 'sciOptions',
            'fgiOptions', 'classificationDate', 'declassificationDate', 'isRawSigint', 'isEvaluatedAndMinimized',
            'isDisseminable', 'lacOptions', 'accmValue', 'topicOptions', 'regionOptions', 'coiOptions', 'brfs', 'organization',
            'classifiedBy']);
    }

    // Map JSON string to classificationObject
    static parse(jsonString: string): ClassificationObject {
        const data = JSON.parse(jsonString);
        return new ClassificationObject(data.classification, data.banner, data.disseminationOptions, data.relToOptions, data.sciOptions, data.fgiOptions,
            data.classificationDate, data.declassificationDate, data.isRawSigint, data.isEvaluatedAndMinimized, data.isDisseminable, data.lacOptions,
            data.accmValue, data.topicOptions, data.regionOptions, data.coiOptions, data.brfs, data.organization, data.classifiedBy);
    }

    // Map JSON string to classificationObject
    parseFromJSON(jsonString: string): ClassificationObject {
        return ClassificationObject.parse(jsonString);
    }

    // Update classificationObject values via JSON String
    updateFromJSON(jsonString: string): void {
        const data = JSON.parse(jsonString);
        this.setClassification(data.classification);
        this.setBanner(data.banner);
        this.setDisseminationOptions(data.disseminationOptions);
        this.setRelToOptions(data.relToOptions);
        this.setSCIOptions(data.sciOptions);
        this.setFGIOptions(data.fgiOptions);
        this.setClassificationDate(data.classificationDate);
        this.setDeClassificationDate(data.declassificationDate);
        this.setIsRawSigint(data.isRawSigint);
        this.setIsEvaluatedAndMinimized(data.isEvaluatedAndMinimized);
        this.setIsDisseminable(data.isDisseminable);
        this.setLACOptions(data.lacOptions);
        this.setACCMValue(data.accmValue);
        this.setTopicOptions(data.topicOptions);
        this.setRegionOptions(data.regionOptions);
        this.setCOIOptions(data.coiOptions);
        this.setBRFs(data.brfs);
        this.setOrganization(data.organization);
        this.setClassifiedBy(data.classifiedBy);
    }

    toClassificationXMLJSONObject(): string {
        const jsonData = {};

        // class:Classification
        const class_Classification = {};

        // class:Classification: _attributes
        const class_Classification_attributes = {
            'xmlns:class': 'urn:us:gov:cia:enterprise:schema:Classification:2.0',
            'xmlns': 'http://www.w3.org/1999/xhtml',
            'dateClassified': this.getClassificationDate(),
            // portionMarking & caveat were set to false in every instance
            'portionMarking': 'false',
            'caveat': 'false',
        };

        class_Classification['_attributes'] = class_Classification_attributes;

        // class:ClassificationMarking
        const class_ClassificationMarking = {
            '_attributes': {
                'type': 'USClassificationMarking',
                'value': this.getClassification(),
            },
        };

        class_Classification['class:ClassificationMarking'] = class_ClassificationMarking;

        // class:ClassificationHeader
        if (Utilities.getSafe(() => this.getBanner(), undefined) !== undefined) {
            const class_ClassificationHeader = {
                'class:ClassificationBanner': {
                    '_text': this.getBanner(),
                },
            };
            class_Classification['class:ClassificationHeader'] = class_ClassificationHeader;
        }

        // class:ClassificationBlock
        const class_ClassificationBlock = {};

        const class_DeclOn = {};
        if (Utilities.getSafe(() => this.getDeClassificationDate(), undefined) !== undefined) {
            const class_DeclOn_attributes = {
                'type': Utilities.isDate(this.getDeClassificationDate())? 'Date' : 'Event',
                // restrictedData is set to false in every scenario
                'restrictedData': 'false',
            };
            class_DeclOn['_attributes'] = class_DeclOn_attributes;
            class_DeclOn['_text'] = this.getDeClassificationDate();
            class_ClassificationBlock['class:DeclOn'] = class_DeclOn;
        }

        const class_ClBy = {};
        if (Utilities.getSafe(() => this.getClassifiedBy(), undefined) !== undefined) {
            class_ClBy['_text'] = this.getClassifiedBy();
            class_ClassificationBlock['class:clBy'] = class_ClBy;
        }
        class_Classification['class:ClassificationBlock'] = class_ClassificationBlock;

        // class:ControlMarkings
        // SCI Options
        let class_ControlMarkings_SCI;
        if (Utilities.getSafe(() => this.getSCIOptions(), undefined) !== undefined) {
            let scis = '';
            if (this.getSCIOptions()!.length !== 0) {
                scis = this.getSCIOptions()!.map( (sci) => {
                    if (sci.startsWith('TK')) {
                        return '{"_attributes":{"value": "' + sci + '", "groupSource": "NRO"}}';
                    } else {
                        return '{"_attributes":{"value": "' + sci + '", "groupSource": "NSA"}}';
                    }
                }).join(', ');

                class_ControlMarkings_SCI = {
                    '_attributes': {
                        'type': 'SCIControls',
                    },
                    'class:ControlMarking': JSON.parse('['+scis+']'),
                };
            }
        }

        // FGI Options
        let class_ControlMarkings_FGI;
        if (Utilities.getSafe(() => this.getFGIOptions(), undefined) !== undefined) {
            let fgis = '';
            if (this.getFGIOptions()!.length !== 0) {
                const fgiOptions = this.getFGIOptions() || [];
                const concealedIdx = fgiOptions.indexOf('CONCEALED');
                if (concealedIdx > -1) {
                    fgiOptions.splice(concealedIdx, 1);
                }
                fgis = fgiOptions!.map( (fgi) => '{"_attributes":{"value": "' + fgi + '"}}').join(', ');
            }

            class_ControlMarkings_FGI = {
                '_attributes': {
                    'type': 'FGIMarkings',
                },
                'class:ControlMarking': {
                    '_attributes': {
                        'value': 'FGI',
                        'groupSource': 'Respective Country',
                    },
                },
            };

            if (fgis.length > 0) {
                class_ControlMarkings_FGI['class:ControlMarking']['class:ControlMarking'] = JSON.parse('['+fgis+']');
            }
        }

        // DisseminationControls
        let class_ControlMarkings_Dissemination;
        let disseminationControls;
        const disseminationItems = Utilities.getSafe(() => this.getDisseminationOptions(), undefined);
        if (disseminationItems !== undefined) {
            disseminationControls = disseminationItems.map( (dissemItem) => {
                if (dissemItem === 'REL TO') {
                    const rels = this.getRelToOptions()!.map( (rel) => {
                        if (rel === 'USA') {
                            return '{ "_attributes": { "value": "USA", "type": "CAPCOTrigraphs" }}';
                        } else {
                            return '{ "_attributes": { "value": "' + rel + '" } }';
                        }
                    }).join(', ');
                    const relsConCat = this.getRelToOptions()!.map( (rel) => rel).join(', ');
                    return '{"_attributes":{"value": "REL TO", "groupSource": "DNI"}, "class:ControlMarking":['+rels+']}, '
                        + '{ "_attributes": { "type": "CAPCOTrigraphs", "value": "REL TO '+ relsConCat +'" }}';
                } else {
                    return '{"_attributes":{"value": "' + dissemItem + '", "type": "CAPCOTrigraphs"}}';
                }
            }).join(', ');

            class_ControlMarkings_Dissemination = {'_attributes': {'type': 'DisseminationControls'},
                'class:ControlMarking': JSON.parse('['+disseminationControls+']'),
            };
        }

        // ACCM Value
        // TODO: Verify if ACCM- must be prepended still
        let class_ControlMarkings_ACCM;
        if (Utilities.getSafe(() => this.getACCMValue(), undefined) !== undefined) {
            class_ControlMarkings_ACCM = {
                '_attributes': {
                    'type': 'NonICMarkings',
                },
                'class:ControlMarking': {
                    '_attributes': {
                        'value': 'ACCM-' + this.getACCMValue(),
                        'groupSource': 'OtherNonPublished',
                    },
                },
            };
        }

        // Include ControlMarkings only if SCI, FGI, or Disseminations values exist
        if (class_ControlMarkings_SCI !== undefined || class_ControlMarkings_FGI !== undefined || class_ControlMarkings_Dissemination !== undefined || class_ControlMarkings_ACCM !== undefined) {
            const class_ControlMarkings: string[] = [];
            if (class_ControlMarkings_SCI !== undefined) {
                class_ControlMarkings.push(class_ControlMarkings_SCI);
            }

            if (class_ControlMarkings_FGI !== undefined) {
                class_ControlMarkings.push(class_ControlMarkings_FGI);
            }

            if (class_ControlMarkings_Dissemination !== undefined) {
                class_ControlMarkings.push(class_ControlMarkings_Dissemination);
            }

            if (class_ControlMarkings_ACCM !== undefined) {
                class_ControlMarkings.push(class_ControlMarkings_ACCM);
            }
            class_Classification['class:ControlMarkings'] = class_ControlMarkings;
        }

        // class:ClassificationFooter
        if (Utilities.getSafe(() => this.getBanner(), undefined) !== undefined) {
            const class_ClassificationFooter = {
                'class:ClassificationBanner': {
                    '_text': this.getBanner(),
                },
            };
            class_Classification['class:ClassificationFooter'] = class_ClassificationFooter;
        }

        // class:Intel
        const hasIntel = (Utilities.getSafe(() => this.getIsRawSigint(), undefined) !== undefined && this.getIsRawSigint() ||
            Utilities.getSafe(() => this.getIsEvaluatedAndMinimized(), undefined) !== undefined && this.getIsEvaluatedAndMinimized() ||
            Utilities.getSafe(() => this.getIsDisseminable(), undefined) !== undefined && this.getIsDisseminable());
        if (hasIntel) {
            class_Classification['class:Intel'] = {
                '_attributes': {
                    'type': 'SIGINT',
                    'value': 'true',
                },
            };

            // class:IsEvaluatedAndMinimized
            class_Classification['class:Intel']['class:IsEvaluatedAndMinimized'] = {
                '_attributes': {
                    'value': this.getIsEvaluatedAndMinimized() ? this.getIsEvaluatedAndMinimized()!.toString() : 'false',
                },
            };

            // class:IsDisseminable
            class_Classification['class:IsDisseminable'] = {
                '_attributes': {
                    'value': this.getIsDisseminable() ? this.getIsDisseminable()!.toString() : 'false',
                },
            };
        }

        // NeedToKnow
        const NeedToKnow = {};

        // LACs
        if (Utilities.getSafe(() => this.getLACOptions(), undefined) !== undefined) {
            let lacs = '';
            if (this.getLACOptions()!.length !== 0) {
                lacs = this.getLACOptions()!.map( (lac) => '{"_text": "' + lac + '"}').join(', ');
            }
            NeedToKnow['LACs'] = {'LAC': JSON.parse('['+lacs+']')};
        }

        NeedToKnow['NIPF']= {};
        // Topics
        if (this.getTopicOptions()) {
            let topics = '';
            if (this.getTopicOptions()!.length !== 0) {
                topics += this.getTopicOptions()!.map( (topic) => '{"_text": "' + topic + '"}').join(', ');
            }
            NeedToKnow['NIPF']['Topics'] = {'Topic': JSON.parse('['+topics+']')};
        }

        // Regions
        if (this.getRegionOptions()) {
            let regions = '';
            if (this.getRegionOptions()!.length !== 0) {
                regions += this.getRegionOptions()!.map( (region) => '{"_text": "' + region + '"}').join(', ');
            }
            NeedToKnow['NIPF']['Regions'] = {'Region': JSON.parse('['+regions+']')};
        }

        // COIs
        if (Utilities.getSafe(() => this.getCOIOptions(), undefined) !== undefined) {
            let cois = '';
            if (this.getCOIOptions()!.length !== 0) {
                cois = this.getCOIOptions()!.map( (coi) => '{"_text": "' + coi + '"}').join(', ');
            }
            NeedToKnow['COIs'] = {'COI': JSON.parse('['+cois+']')};
        }

        // BRFs
        if (Utilities.getSafe(() => this.getBRFs(), undefined) !== undefined) {
            let brfs = '';
            if (this.getBRFs()!.length !== 0) {
                brfs = this.getBRFs()!.map( (brf) => '{"_text": "' + brf + '"}').join(', ');
            }
            NeedToKnow['BRFs'] = {'BRF': JSON.parse('['+brfs+']')};
        }

        // Organization
        if (Utilities.getSafe(() => this.getOrganization(), undefined) !== undefined) {
            NeedToKnow['Organization'] = {'_text': this.getOrganization()};
        }

        class_Classification['NeedToKnow'] = NeedToKnow;

        // Set Final JSON Object
        jsonData['class:Classification'] = class_Classification;
        return JSON.stringify(jsonData);
    }

    // Return XML String representation of ClassificationObject
    toClassificationXMLString(): string {
        return Utilities.removeLineBreaks(Utilities.jsonToXML(this.toClassificationXMLJSONObject()));
    }
}
